import React from 'react';
import xor from 'lodash/xor';
import intersection from 'lodash/intersection';
import union from 'lodash/union';

// mui
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 'auto',
  },
  cardHeader: {
    padding: theme.spacing(1, 2),
  },
  list: {
    width: '100%',
    height: 460,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
}));

export const TransferList = ({
  leftData,
  rightData,
  listComponent,
  handleUpdateAssignedData,
  leftTitle = 'Choices',
  rightTitle = 'Chosen',
  spacing = 2,
}) => {
  const classes = useStyles();
  const [checked, setChecked] = React.useState([]);
  const [left, setLeft] = React.useState(leftData);
  const [right, setRight] = React.useState(rightData);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  React.useEffect(() => {
    handleUpdateAssignedData(left);
  }, [handleUpdateAssignedData, left]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(xor(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(xor(left, leftChecked));
    setChecked(xor(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(xor(right, rightChecked));
    setChecked(xor(checked, rightChecked));
  };

  const customList = (title, items, testid) => (
    <Card data-testid={`transfer-list-card-${testid}`}>
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
            disabled={items.length === 0}
            inputProps={{ 'aria-label': 'all items selected' }}
            color="primary"
            data-testid="transfer-list-checkbox-all"
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
      />
      <Divider />
      <List className={classes.list} dense component="div" role="list">
        {items.map((item) => {
          const labelId = `transfer-list-all-item-${item.id}-label`;

          return (
            <ListItem key={item.id} role="listitem" button onClick={handleToggle(item)} data-testid={`list-item-${testid}`}>
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(item) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                  color="primary"
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={item.name} />
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Card>
  );

  return (
    <Grid container spacing={spacing} justifyContent="center" alignItems="center" className={classes.root} data-testid="transfer-list">
      <Grid style={{ width: 'calc(50% - 60px)' }} data-testid="transfer-list-left">
        {listComponent
          ? listComponent({ title: leftTitle, items: left, checked, numberOfChecked, handleToggle, handleToggleAll, testid: 'left' })
          : customList(leftTitle, left, 'left')}
      </Grid>
      <Grid item style={{ width: '100px' }}>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item style={{ width: 'calc(50% - 60px)' }} data-testid="transfer-list-right">
        {listComponent
          ? listComponent({ title: rightTitle, items: right, checked, numberOfChecked, handleToggle, handleToggleAll, testid: 'right' })
          : customList(rightTitle, right, 'right')}
      </Grid>
    </Grid>
  );
};
